<template>
  <PageTemplate>
    <h1>
      Calendar
    </h1>
    <fieldset class="fieldset">
      <v-input-wrap prefix="search">
        <v-input
          type="search"
          @input="performSearch"
        />
      </v-input-wrap>
    </fieldset>
    <template v-if="isSearchMode">
      <template v-if="searchResults.length">
        <h3>
          Showing future meeting events related to "{{ searchKeyword }}".
        </h3>
        <v-notification
          v-for="(event, index) in searchResults"
          :key="index"
          icon="clock"
          :text="event.title"
          :date="event.formatDate"
          :time="event.formatTime"
          :all-day="event.allDay"
          style="cursor:pointer;"
          @click="searchResultNavigator(event)"
        />
      </template>
      <h3 v-else>
        No future meeting events related to "{{ searchKeyword }}" found.
      </h3>
    </template>
    <v-card-body v-show="!isSearchMode">
      <v-calendar
        ref="calendar"
        default-view="dayGridMonth"
        :plugins="calendarPlugins"
        :events="events"
        :selectable="true"
        :editable="true"
        :event-limit="true"
        :aspect-ratio="1.5"
        :default-date="defaultDate"
        :header="{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek'
        }"
        @eventClick="handleEventClick"
        @select="handleSelect"
        @eventDrop="handleUpdate"
        @eventResize="handleUpdate"
      />
    </v-card-body>
    <v-modal
      v-if="showModal"
      @close="closeModal"
    >
      <template #header>
        <template v-if="currentEvent">
          Update event
        </template>
        <template v-else>
          Create new event
        </template>
      </template>
      <v-fieldset
        id="eventName"
        v-model="eventName"
        text="Event Name"
        is-required
        :readonly="!isCommitteeAdmin"
      />
      <v-fieldset
        id="eventDateStart"
        v-model="startDate"
        :type="allDay ? 'date': 'datetime-local'"
        text="Event Start Date"
        is-required
        :readonly="!isCommitteeAdmin"
      />
      <v-fieldset
        id="eventDateEnd"
        v-model="endDate"
        :type="allDay ? 'date': 'datetime-local'"
        text="Event End Date"
        is-required
        :readonly="!isCommitteeAdmin"
      />
      <v-checkbox
        v-if="isCommitteeAdmin"
        id="allDay"
        :checked="allDay"
        @change="changeAllDay"
      >
        All day event
      </v-checkbox>
      <template #footer>
        <v-button-group is-right>
          <v-button
            v-if="isCreatedBy()"
            size="lg"
            has-min-width
            @click="updateSelectedEvent"
          >
            Update
          </v-button>
          <v-button
            v-if="isCreatedBy()"
            color="danger"
            size="lg"
            has-min-width
            @click="removeEvent"
          >
            Delete
          </v-button>
          <v-button
            v-else-if="!currentEvent"
            size="lg"
            has-min-width
            @click="createEvent"
          >
            Create
          </v-button>
          <v-button
            color="secondary"
            size="lg"
            has-min-width
            @click="closeModal"
          >
            Close
          </v-button>
        </v-button-group>
      </template>
    </v-modal>
  </PageTemplate>
</template>
<script>
  import PageTemplate from "@/templates/Page";
  import {buildBreadCrumb} from "@/common/tableFunctions";
  import {checkIfCommitteeAdmin} from "@/common/switchCommittee";
  import FullCalendar from '@fullcalendar/vue';
  import CardBody from "@/atoms/CardBody";
  import ButtonGroup from "@/molecules/ButtonGroup";
  import Fieldset from "@/molecules/Fieldset";
  import Modal from "@/atoms/Modal";
  import Button from "@/atoms/Button";
  import Checkbox from "@/atoms/Checkbox";
  import {GET_EVENTS, ADD_EVENT, DELETE_EVENT, UPDATE_EVENT} from "../store/actions.type";
  import {mapState} from "vuex";
  import InputWithIcon from "@/molecules/InputWithIcon";
  import Input from "@/atoms/Input";
  import Notification from "@/atoms/Notification";
  import routes from "@/common/routes";

  //calendar plugins
  import dayGrid from '@fullcalendar/daygrid';
  import timeGrid from '@fullcalendar/timegrid';
  import interaction from '@fullcalendar/interaction';
  import moment from 'moment';
  import {formatDateToUTC} from "../common/formatting";

  export default {
    name: "Calendar",
    components: {
      PageTemplate,
      'v-calendar': FullCalendar,
      'v-card-body': CardBody,
      'v-modal': Modal,
      'v-button': Button,
      'v-checkbox': Checkbox,
      'v-button-group': ButtonGroup,
      'v-fieldset': Fieldset,
      "v-input": Input,
      "v-input-wrap": InputWithIcon,
      'v-notification': Notification
    },
    props: {
      highlightedEvent: {
        type: String,
        default: null
      },
    },
    data() {
      return {
        calendarPlugins: [dayGrid, timeGrid, interaction],
        currentEvent: null,
        showModal: false,
        eventName: '',
        eventDateStart: '',
        eventDateEnd: '',
        allDay: false,
        defaultDate: this.$route.params.date,
        searchKeyword: '',
        isSearchMode: false,
        searchResults: [],
        isCommitteeAdmin: false
      }
    },
    computed: {
      ...mapState({
        events: state => state.committees.events,
        selectedCommittee: state => state.auth.selectedCommittee,
      }),
      endDate: {
        get() {
          return this.allDay ? moment(this.eventDateEnd).add(-1, 'days').format(this.formatDateString()) : moment(this.eventDateEnd).format(this.formatDateString())
        },
        set(val) {
          this.eventDateEnd = this.allDay ? moment(val).add(1, 'days') : moment(val)
        },
      },
      startDate: {
        get() {
          return moment(this.eventDateStart).format(this.formatDateString())
        },
        set(val) {
          this.eventDateStart = moment(val)
        },
      },
      checkIfCommitteeAdmin,
    },
    watch: {
      selectedCommittee: {
        handler(val, oldVal) {
          this.getEvents(val);
        }
      },
      '$route.params.date': function (date) {
        this.jumpToDate(this.getSelectedDate());
      }
    },
    mounted() {
      this.updateBreadCrumb();
      this.getEvents(this.selectedCommittee);
      this.scrollToEvent();
    },
    methods: {
      getSelectedDate() {
        let dateFromQuery = this.$route.params.date;
        if (dateFromQuery) {
          dateFromQuery = new Date(dateFromQuery);
        } else {
          dateFromQuery = new Date();
        }
        return dateFromQuery;
      },
      getEvents(val) {
        this.$store.dispatch(GET_EVENTS, val).then(response => { this.jumpToDate(this.getSelectedDate()); });
        this.isCommitteeAdmin = this.checkIfCommitteeAdmin;
      },
      updateBreadCrumb: function () {
        let breadcrumbs = [];
        breadcrumbs['Dashboard'] = routes.DASHBOARD;
        breadcrumbs['Calendar'] = '';
        this.$store.dispatch('updateBreadCrumbAction', buildBreadCrumb(breadcrumbs));
      },
      jumpToDate(date) {
        this.$refs.calendar.getApi().gotoDate(date);
        this.scrollToEvent();
      },
      scrollToEvent() {
        let xx = document.getElementsByClassName('fc-title');
        xx = xx[xx.length - 1];
        if (xx) {
          setTimeout(() => {
            xx.scrollIntoView();
          }, 100);
        }
      },
      handleUpdate(e) {
        let event_id = e.event.id;
        let newStart = moment(e.event.start).format(this.formatDateString());

        var params = {
          committee_id: this.selectedCommittee,
          title: e.event.title,
          start: moment(newStart).format(this.formatDateString()),
          allDay: this.allDay
        };
        if (e.event.end) {
          let newEnd = moment(e.event.end).format(this.formatDateString());
          params.end = moment(newEnd).format(this.formatDateString())
        } else {
          if (e.event.allDay) {
            let newEnd = moment(e.event.start).add('1', 'days').format(this.formatDateString());
            params.end = moment(newEnd).format(this.formatDateString())
            params.allDay = e.event.allDay;
          } else {
            let newEnd = moment(e.event.start).add('30', 'minutes').format(this.formatDateString());
            params.end = moment(newEnd).format(this.formatDateString())
          }
        }
        this.updateEvent(event_id, params);
      },
      updateSelectedEvent() {
        let newStart = moment(this.eventDateStart).format(this.formatDateString());
        let newEnd = moment(this.eventDateEnd).format(this.formatDateString());
        var params = {
          committee_id: this.selectedCommittee,
          title: this.eventName,
          start: moment(newStart).format(this.formatDateString()),
          end: moment(newEnd).format(this.formatDateString()),
          allDay: this.allDay
        };
        this.updateEvent(this.currentEvent.id, params)
      },
      formatDateString() {
        return this.allDay ? "YYYY-MM-DD" : "YYYY-MM-DDTHH:mm"
      },

      //create new event
      handleSelect(e) {
        if (this.isCommitteeAdmin) {
          this.allDay = e.allDay;
          this.eventDateStart = moment(e.start);
          this.eventDateEnd = moment(e.end);
          this.showModal = true;
        }
      },

      //click on existing event
      handleEventClick(e) {
        this.currentEvent = e.event;
        this.allDay = e.event.allDay;
        this.eventName = e.event.title;
        this.eventDateStart = moment(e.event.start);
        this.eventDateEnd = e.event.end ? moment(e.event.end) : moment(e.event.start);
        this.showModal = true;
      },

      isCreatedBy() {
        if (this.currentEvent) {
          let e = this.events.find(event => event.id == this.currentEvent.id);
          return e.isCreatedBy;
        } else
          return false;
      },

      //remove event
      removeEvent() {
        this.$store.dispatch(DELETE_EVENT, this.currentEvent.id).then(response => {
          if (response.success) {
            this.$delete(this.events, this.events.findIndex(event => event.id == this.currentEvent.id));
            this.closeModal();
            this.getEvents(this.selectedCommittee)
          } else {
            this.$notify({
              title: 'Error',
              text: "You can not delete this event!",
              type: 'error'
            });
          }
        });
      },
      closeModal() {
        this.showModal = false;
        this.currentEvent = null;
        this.eventName = '';
      },
      changeAllDay() {
        this.allDay = !this.allDay;
      },
      createEvent() {
        let newStart = moment(this.eventDateStart).format(this.formatDateString());
        let newEnd = moment(this.eventDateEnd).format(this.formatDateString());
        if (this.eventName.length === 0) {
          this.$notify({
            title: 'Error',
            text: "Please enter an event name",
            type: 'error'
          });
        }
        else if (newStart >= newEnd) {
          this.$notify({
            title: 'Error',
            text: "Event end date can't be in the past",
            type: 'error'
          });
        } else {
          /**
           * if allDay = false, convert the time to UTC and store in DB
           * @type {any}
           */
          var params = {
            committee_id: this.selectedCommittee,
            title: this.eventName,
            start: this.allDay ? moment(newStart).format(this.formatDateString()) : formatDateToUTC(newStart, this.formatDateString()),
            end: this.allDay ? moment(newEnd).format(this.formatDateString()) : formatDateToUTC(newEnd, this.formatDateString()),
            allDay: this.allDay
          };
          this.$store.dispatch(ADD_EVENT, params).then(response => {
            if (response.success) {
              this.getEvents(this.selectedCommittee)
            } else {
              this.$notify({
                title: 'Error:',
                text: 'Only Committee Admin can create an event!',
                type: 'error'
              })
            }
          });
          this.closeModal();
        }
      },
      updateEvent(event_id, params) {
        /**
         * if allDay = false, convert the time to UTC and store in DB
         * @type {any}
         */
        if (params.allDay === false || params.allDay === 0) {
          params.start = formatDateToUTC(params.start, this.formatDateString());
          params.end = formatDateToUTC(params.end, this.formatDateString());
        }
        this.$store.dispatch(UPDATE_EVENT, {event_id, params}).then(response => {
          if (response.success) {
            this.getEvents(this.selectedCommittee)
            this.closeModal();
          } else {
            this.$notify({
              title: 'Error:',
              text: response.response.data.data,
              type: 'error'
            })
          }
        });
      },
      performSearch: function (val) {
        this.searchKeyword = val;
        if (val.length === 0) {
          this.isSearchMode = false;
        }
        else {
          let searchKey = this.searchKeyword.toLowerCase();
          this.isSearchMode = (this.searchKeyword.length === 0 ? false : true);
          // clear search - jump back to current month.
          if (!this.isSearchMode) {
            this.jumpToDate(new Date());
            return;
          }
          this.searchResults = this.events.filter(evt => {
            if (evt.title.toLowerCase().indexOf(searchKey) !== -1 &&
              this.isFutureEvent(evt.start)) {
              return evt;
            }
          });
        }
      },
      isFutureEvent(evtDate) {
        const date = moment(evtDate);
        const now = moment();
        return (now < date);
      },
      searchResultNavigator(event) {
        this.isSearchMode = false;
        this.jumpToDate(event.start);
      }
    },

  }
</script>

<style lang='scss'>
  @import "../styles/shared/shared";
  @import '~@fullcalendar/daygrid/main.css';
  @import '~@fullcalendar/timegrid/main.css';

  @font-face {
    font-family: "fcicons";
    src: url("data:application/x-font-ttf;charset=utf-8;base64,AAEAAAALAIAAAwAwT1MvMg8SBfAAAAC8AAAAYGNtYXAXVtKNAAABHAAAAFRnYXNwAAAAEAAAAXAAAAAIZ2x5ZgYydxIAAAF4AAAFNGhlYWQUJ7cIAAAGrAAAADZoaGVhB20DzAAABuQAAAAkaG10eCIABhQAAAcIAAAALGxvY2ED4AU6AAAHNAAAABhtYXhwAA8AjAAAB0wAAAAgbmFtZXsr690AAAdsAAABhnBvc3QAAwAAAAAI9AAAACAAAwPAAZAABQAAApkCzAAAAI8CmQLMAAAB6wAzAQkAAAAAAAAAAAAAAAAAAAABEAAAAAAAAAAAAAAAAAAAAABAAADpBgPA/8AAQAPAAEAAAAABAAAAAAAAAAAAAAAgAAAAAAADAAAAAwAAABwAAQADAAAAHAADAAEAAAAcAAQAOAAAAAoACAACAAIAAQAg6Qb//f//AAAAAAAg6QD//f//AAH/4xcEAAMAAQAAAAAAAAAAAAAAAQAB//8ADwABAAAAAAAAAAAAAgAANzkBAAAAAAEAAAAAAAAAAAACAAA3OQEAAAAAAQAAAAAAAAAAAAIAADc5AQAAAAABAWIAjQKeAskAEwAAJSc3NjQnJiIHAQYUFwEWMjc2NCcCnuLiDQ0MJAz/AA0NAQAMJAwNDcni4gwjDQwM/wANIwz/AA0NDCMNAAAAAQFiAI0CngLJABMAACUBNjQnASYiBwYUHwEHBhQXFjI3AZ4BAA0N/wAMJAwNDeLiDQ0MJAyNAQAMIw0BAAwMDSMM4uINIwwNDQAAAAIA4gC3Ax4CngATACcAACUnNzY0JyYiDwEGFB8BFjI3NjQnISc3NjQnJiIPAQYUHwEWMjc2NCcB87e3DQ0MIw3VDQ3VDSMMDQ0BK7e3DQ0MJAzVDQ3VDCQMDQ3zuLcMJAwNDdUNIwzWDAwNIwy4twwkDA0N1Q0jDNYMDA0jDAAAAgDiALcDHgKeABMAJwAAJTc2NC8BJiIHBhQfAQcGFBcWMjchNzY0LwEmIgcGFB8BBwYUFxYyNwJJ1Q0N1Q0jDA0Nt7cNDQwjDf7V1Q0N1QwkDA0Nt7cNDQwkDLfWDCMN1Q0NDCQMt7gMIw0MDNYMIw3VDQ0MJAy3uAwjDQwMAAADAFUAAAOrA1UAMwBoAHcAABMiBgcOAQcOAQcOARURFBYXHgEXHgEXHgEzITI2Nz4BNz4BNz4BNRE0JicuAScuAScuASMFITIWFx4BFx4BFx4BFREUBgcOAQcOAQcOASMhIiYnLgEnLgEnLgE1ETQ2Nz4BNz4BNz4BMxMhMjY1NCYjISIGFRQWM9UNGAwLFQkJDgUFBQUFBQ4JCRULDBgNAlYNGAwLFQkJDgUFBQUFBQ4JCRULDBgN/aoCVgQIBAQHAwMFAQIBAQIBBQMDBwQECAT9qgQIBAQHAwMFAQIBAQIBBQMDBwQECASAAVYRGRkR/qoRGRkRA1UFBAUOCQkVDAsZDf2rDRkLDBUJCA4FBQUFBQUOCQgVDAsZDQJVDRkLDBUJCQ4FBAVVAgECBQMCBwQECAX9qwQJAwQHAwMFAQICAgIBBQMDBwQDCQQCVQUIBAQHAgMFAgEC/oAZEhEZGRESGQAAAAADAFUAAAOrA1UAMwBoAIkAABMiBgcOAQcOAQcOARURFBYXHgEXHgEXHgEzITI2Nz4BNz4BNz4BNRE0JicuAScuAScuASMFITIWFx4BFx4BFx4BFREUBgcOAQcOAQcOASMhIiYnLgEnLgEnLgE1ETQ2Nz4BNz4BNz4BMxMzFRQWMzI2PQEzMjY1NCYrATU0JiMiBh0BIyIGFRQWM9UNGAwLFQkJDgUFBQUFBQ4JCRULDBgNAlYNGAwLFQkJDgUFBQUFBQ4JCRULDBgN/aoCVgQIBAQHAwMFAQIBAQIBBQMDBwQECAT9qgQIBAQHAwMFAQIBAQIBBQMDBwQECASAgBkSEhmAERkZEYAZEhIZgBEZGREDVQUEBQ4JCRUMCxkN/asNGQsMFQkIDgUFBQUFBQ4JCBUMCxkNAlUNGQsMFQkJDgUEBVUCAQIFAwIHBAQIBf2rBAkDBAcDAwUBAgICAgEFAwMHBAMJBAJVBQgEBAcCAwUCAQL+gIASGRkSgBkSERmAEhkZEoAZERIZAAABAOIAjQMeAskAIAAAExcHBhQXFjI/ARcWMjc2NC8BNzY0JyYiDwEnJiIHBhQX4uLiDQ0MJAzi4gwkDA0N4uINDQwkDOLiDCQMDQ0CjeLiDSMMDQ3h4Q0NDCMN4uIMIw0MDOLiDAwNIwwAAAABAAAAAQAAa5n0y18PPPUACwQAAAAAANivOVsAAAAA2K85WwAAAAADqwNVAAAACAACAAAAAAAAAAEAAAPA/8AAAAQAAAAAAAOrAAEAAAAAAAAAAAAAAAAAAAALBAAAAAAAAAAAAAAAAgAAAAQAAWIEAAFiBAAA4gQAAOIEAABVBAAAVQQAAOIAAAAAAAoAFAAeAEQAagCqAOoBngJkApoAAQAAAAsAigADAAAAAAACAAAAAAAAAAAAAAAAAAAAAAAAAA4ArgABAAAAAAABAAcAAAABAAAAAAACAAcAYAABAAAAAAADAAcANgABAAAAAAAEAAcAdQABAAAAAAAFAAsAFQABAAAAAAAGAAcASwABAAAAAAAKABoAigADAAEECQABAA4ABwADAAEECQACAA4AZwADAAEECQADAA4APQADAAEECQAEAA4AfAADAAEECQAFABYAIAADAAEECQAGAA4AUgADAAEECQAKADQApGZjaWNvbnMAZgBjAGkAYwBvAG4Ac1ZlcnNpb24gMS4wAFYAZQByAHMAaQBvAG4AIAAxAC4AMGZjaWNvbnMAZgBjAGkAYwBvAG4Ac2ZjaWNvbnMAZgBjAGkAYwBvAG4Ac1JlZ3VsYXIAUgBlAGcAdQBsAGEAcmZjaWNvbnMAZgBjAGkAYwBvAG4Ac0ZvbnQgZ2VuZXJhdGVkIGJ5IEljb01vb24uAEYAbwBuAHQAIABnAGUAbgBlAHIAYQB0AGUAZAAgAGIAeQAgAEkAYwBvAE0AbwBvAG4ALgAAAAMAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA=") format("truetype");
    font-weight: normal;
    font-style: normal;
  }

  .fc {
    direction: ltr;
    text-align: left;
    table {
      width: 100%;
      box-sizing: border-box;
      table-layout: fixed;
      border-collapse: collapse;
      border-spacing: 0;
      font-size: 1em;
    }
    th {
      text-align: center;
      border-style: solid;
      border-width: 1px;
      padding: 0;
      vertical-align: top;
    }
    td {
      border-style: solid;
      border-width: 1px;
      padding: 0;
      vertical-align: top;
    }
    td.fc-today {
      border-style: double;
    }
    .fc-row {
      border-style: solid;
      border-width: 0;
      .fc-content-skeleton {
        table {
          background: none;
          border-color: transparent;
        }
        td {
          background: none;
          border-color: transparent;
        }
      }
      .fc-mirror-skeleton {
        td {
          background: none;
          border-color: transparent;
        }
      }
    }
  }

  .fc-highlight {
    background-color: $azure;
    opacity: .1;
  }

  .fc-bgevent {
    background: #8fdf82;
    opacity: 0.3;
  }

  .fc-nonbusiness {
    background: #d7d7d7;
  }

  .fc-popover {
    position: absolute;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    .fc-header {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      padding: 2px 4px;
      .fc-title {
        margin: 0 2px;
      }
      .fc-close {
        cursor: pointer;
        opacity: 0.65;
        font-size: 1.1em;
      }
    }
  }

  .fc-divider {
    border-style: solid;
    border-width: 1px;
  }

  hr.fc-divider {
    height: 0;
    margin: 0;
    padding: 0 0 2px;
    border-width: 1px 0;
  }

  .fc-bg {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    table {
      height: 100%;
    }
  }

  .fc-bgevent-skeleton {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
  }

  .fc-highlight-skeleton {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
  }

  .fc-mirror-skeleton {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    tr {
      &:first-child {
        > td {
          > .fc-day-grid-event {
            margin-top: 0;
          }
        }
      }
    }
  }

  a[data-goto] {
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }

  .fc-row {
    table {
      border-left: 0 hidden transparent;
      border-right: 0 hidden transparent;
      border-bottom: 0 hidden transparent;
    }
    &:first-child {
      table {
        border-top: 0 hidden transparent;
      }
    }
    position: relative;
    .fc-bg {
      z-index: 1;
    }
    .fc-bgevent-skeleton {
      bottom: 0;
      z-index: 2;
      table {
        height: 100%;
      }
      td {
        border-color: transparent;
      }
    }
    .fc-highlight-skeleton {
      bottom: 0;
      z-index: 3;
      table {
        height: 100%;
      }
      td {
        border-color: transparent;
      }
    }
    .fc-content-skeleton {
      position: relative;
      z-index: 4;
      padding-bottom: 2px;
      td {
        border-bottom: 0;
      }
      tbody {
        td {
          border-top: 0;
        }
      }
    }
    .fc-mirror-skeleton {
      z-index: 5;
      td {
        border-bottom: 0;
      }
      tbody {
        td {
          border-top: 0;
        }
      }
    }
  }

  .fc-scroller {
    -webkit-overflow-scrolling: touch;
    > .fc-day-grid {
      position: relative;
      width: 100%;
    }
    > .fc-time-grid {
      position: relative;
      width: 100%;
    }
  }

  .fc-event {
    position: relative;
    display: block;
    font-size: 0.85em;
    line-height: 1.4;
    border-radius: 3px;
    border: 1px solid $azure;
    background-color: $azure;
    color: $white;
    text-decoration: none;
    &:hover {
      color: #fff;
      text-decoration: none;
    }
    .fc-content {
      position: relative;
      z-index: 2;
    }
    .fc-resizer {
      position: absolute;
      z-index: 4;
      display: none;
    }
  }

  .fc-event-dot {
    background-color: #3788d8;
  }

  .fc-event[href] {
    cursor: pointer;
  }

  .fc-event.fc-draggable {
    cursor: pointer;
  }

  .fc-not-allowed {
    cursor: not-allowed;
    .fc-event {
      cursor: not-allowed;
    }
  }

  .fc-event.fc-allow-mouse-resize {
    .fc-resizer {
      display: block;
    }
  }

  .fc-event.fc-selected {
    .fc-resizer {
      display: block;
      &:before {
        content: "";
        position: absolute;
        z-index: 9999;
        top: 50%;
        left: 50%;
        width: 40px;
        height: 40px;
        margin-left: -20px;
        margin-top: -20px;
      }
    }
    z-index: 9999 !important;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    &:after {
      content: "";
      position: absolute;
      z-index: 1;
      top: -1px;
      right: -1px;
      bottom: -1px;
      left: -1px;
      background: #000;
      opacity: 0.25;
    }
  }

  .fc-event.fc-dragging.fc-selected {
    box-shadow: 0 2px 7px rgba(0, 0, 0, 0.3);
  }

  .fc-event.fc-dragging {
    &:not(.fc-selected) {
      opacity: 0.75;
    }
  }

  .fc-h-event.fc-selected {
    &:before {
      content: "";
      position: absolute;
      z-index: 3;
      top: -10px;
      bottom: -10px;
      left: 0;
      right: 0;
    }
    .fc-resizer {
      border-radius: 4px;
      border-width: 1px;
      width: 6px;
      height: 6px;
      border-style: solid;
      border-color: inherit;
      background: #fff;
      top: 50%;
      margin-top: -4px;
    }
  }

  .fc-ltr {
    .fc-h-event.fc-not-start {
      margin-left: 0;
      border-left-width: 0;
      padding-left: 1px;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
    .fc-h-event.fc-not-end {
      margin-right: 0;
      border-right-width: 0;
      padding-right: 1px;
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
    .fc-h-event {
      .fc-start-resizer {
        cursor: w-resize;
        left: -1px;
      }
      .fc-end-resizer {
        cursor: e-resize;
        right: -1px;
      }
    }
    .fc-h-event.fc-selected {
      .fc-start-resizer {
        margin-left: -4px;
      }
      .fc-end-resizer {
        margin-right: -4px;
      }
    }
    .fc-day-grid-event.fc-allow-mouse-resize {
      .fc-start-resizer {
        margin-left: -2px;
      }
      .fc-end-resizer {
        margin-right: -2px;
      }
    }
  }

  .fc-h-event.fc-allow-mouse-resize {
    .fc-resizer {
      width: 7px;
      top: -1px;
      bottom: -1px;
    }
  }

  .fc-day-grid-event {
    margin: 1px 2px 0;
    padding: 0 1px;
    .fc-content {
      white-space: nowrap;
      overflow: hidden;
    }
    .fc-time {
      font-weight: bold;
    }
  }

  tr {
    &:first-child {
      > td {
        > .fc-day-grid-event {
          margin-top: 2px;
        }
      }
    }
  }

  a.fc-more {
    margin: 1px 3px;
    font-size: 0.85em;
    cursor: pointer;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }

  .fc-limited {
    display: none;
  }

  .fc-day-grid {
    .fc-row {
      z-index: 1;
    }
  }

  .fc-more-popover {
    z-index: 2;
    width: 220px;
    .fc-event-container {
      padding: 10px;
    }
  }

  .fc-now-indicator {
    position: absolute;
    border: 0 solid red;
  }

  .fc-unselectable {
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    -webkit-touch-callout: none;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  }

  .fc-unthemed {
    th {
      border-color: $pigeon;
      background-color: $zircon;
    }
    td {
      border-color: $pigeon;
    }
    thead {
      border-color: $pigeon;
    }
    tbody {
      border-color: $pigeon;
    }
    .fc-divider {
      border-color: $pigeon;
      background: #eee;
    }
    .fc-row {
      border-color: $pigeon;
    }
    .fc-content {
      border-color: $pigeon;
    }
    .fc-popover {
      border-color: $pigeon;
      background-color: #fff;
      border-width: 1px;
      border-style: solid;
      .fc-header {
        background: #eee;
      }
    }
    .fc-list-view {
      border-color: $pigeon;
    }
    .fc-list-heading {
      td {
        border-color: $pigeon;
        background: #eee;
      }
    }
    td.fc-today {
      background: rgba($tangerine, .2);
    }
    .fc-disabled-day {
      background: #d7d7d7;
      opacity: 0.3;
    }
    .fc-list-item {
      &:hover {
        td {
          background-color: #f5f5f5;
        }
      }
    }
  }

  .fc-icon {
    font-family: "fcicons" !important;
    speak: none;
    font-style: normal;
    font-weight: normal;
    font-variant: normal;
    text-transform: none;
    line-height: 1;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    display: inline-block;
    width: 1em;
    height: 1em;
    text-align: center;
  }

  .fc-icon-chevron-left {
    &:before {
      content: "";
    }
  }

  .fc-icon-chevron-right {
    &:before {
      content: "";
    }
  }

  .fc-icon-chevrons-left {
    &:before {
      content: "";
    }
  }

  .fc-icon-chevrons-right {
    &:before {
      content: "";
    }
  }

  .fc-icon-minus-square {
    &:before {
      content: "";
    }
  }

  .fc-icon-plus-square {
    &:before {
      content: "";
    }
  }

  .fc-icon-x {
    &:before {
      content: "";
    }
  }

  .fc-button {
    padding: 5px 10px;
    font-weight: 700;
    outline: none;
    display: inline-block;
    border-radius: 4px;
    cursor: pointer;
    font-size: $font-size-base;
    box-shadow: none;
    border-style: solid;
    border-width: 1px;
    text-transform: capitalize;
    @include transitionOut();
    &:hover {
      @include transitionIn();
      text-decoration: none;
    }
    &:focus {
      outline: none;
    }
    &,
    &:hover {
      color: $white;
    }
    &:disabled {
      pointer-events: none;
      color: $casper;
      border-color: $casper;
      background: $zircon;
    }
    &:focus {
      outline: 0;
    }
    &:not(:disabled) {
      cursor: pointer;
    }
    &::-moz-focus-inner {
      padding: 0;
      border-style: none;
    }
    &:hover {
      text-decoration: none;
    }
    .fc-icon {
      vertical-align: middle;
    }
  }

  .fc-button-primary {
    color: $white;
    background-color: $btn-primary;
    border-color: $btn-primary;
    &:hover {
      background-color: $btn-primary-hover;
      border-color: $btn-primary-hover;
    }
    &:active:focus {
      background-color: $btn-primary-active;
      border-color: $btn-primary-active;
    }
    &:not(:disabled) {
      &:active {
        color: $white;
        background-color: $btn-primary-active;
        border-color: $btn-primary-active;
        &:focus {
        }
      }
    }
    &:not(:disabled).fc-button-active {
      color: $white;
      background-color: $btn-primary-active;
      border-color: $btn-primary-active;
      &:focus {
      }
    }
  }

  .fc-button-group {
    position: relative;
    display: inline-flex;
    > .fc-button {
      position: relative;
      flex: 1 1 auto;
      &:hover {
        z-index: 1;
      }
      &:focus {
        z-index: 1;
      }
      &:active {
        z-index: 1;
      }
      &:not(:first-child) {
        margin-left: -1px;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
      &:not(:last-child) {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
    > .fc-button.fc-button-active {
      z-index: 1;
    }
  }

  .fc-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    > * > {
      :not(:first-child) {
        margin-left: 0.75em;
      }
    }
    h2 {
      font-size: 1.75em;
      margin: 0;
    }
  }

  .fc-toolbar.fc-header-toolbar {
    margin-bottom: 1.5em;
  }

  .fc-toolbar.fc-footer-toolbar {
    margin-top: 1.5em;
  }

  .fc-past {
    background-color: rgba($zircon, .5);
  }

  .fc-view-container {
    position: relative;
    * {
      box-sizing: content-box;
      &:before {
        box-sizing: content-box;
      }
      &:after {
        box-sizing: content-box;
      }
    }
  }

  .fc-view {
    position: relative;
    z-index: 1;
    > table {
      position: relative;
      z-index: 1;
    }
  }

</style>
